/**
 * <read-more> Custom Element
 *
 * Attributes:
 *   lines        — max lines to show (default: 0 = disabled)
 *   chars        — max characters to show (default: 0 = disabled)
 *   label-more   — button label when collapsed (default: "Read more")
 *   label-less   — button label when expanded  (default: "Read less")
 *
 * Priority: if both lines & chars are set, `lines` takes precedence.
 * If neither is set (both 0), the element is transparent (no clamping).
 *
 * Works with dynamic DOM (e.g. product recommendations rendered via API)
 * because it uses a MutationObserver on the document body.
 */

class ReadMore extends HTMLElement {
  constructor() {
    super();
    this._expanded = false;
    this._initialized = false;
    this._needsClamp = false;
  }

  /* ─── Lifecycle ─────────────────────────────────────────────── */

  connectedCallback() {
    // Defer so slotted/innerHTML content is fully parsed
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this._init(), { once: true });
    } else {
      // Use rAF so layout is ready (needed for line-height measurement)
      requestAnimationFrame(() => this._init());
    }
  }

  disconnectedCallback() {
    this._cleanup();
  }

  /* ─── Public ─────────────────────────────────────────────────── */

  get lines() { return parseInt(this.getAttribute('lines') || '0', 10); }
  get chars() { return parseInt(this.getAttribute('chars') || '0', 10); }
  get labelMore() { return this.getAttribute('label-more') || 'Read more'; }
  get labelLess() { return this.getAttribute('label-less') || 'Read less'; }

  /* ─── Init ───────────────────────────────────────────────────── */

  _init() {
    if (this._initialized) return;

    const maxLines = this.lines;
    const maxChars = this.chars;

    // Nothing to do
    if (maxLines <= 0 && maxChars <= 0) return;

    this._content = this.querySelector('.read-more__content');
    if (!this._content) return;

    // Measure whether clamping is actually needed
    this._needsClamp = maxLines > 0
      ? this._exceedsLines(maxLines)
      : this._exceedsChars(maxChars);

    if (!this._needsClamp) return;

    this._applyClamp(maxLines, maxChars);
    this._buildButton();
    this._initialized = true;
  }

  /* ─── Clamping helpers ───────────────────────────────────────── */

  _exceedsLines(maxLines) {
    const el = this._content;
    const style = getComputedStyle(el);
    const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.4;
    const maxHeight = lineHeight * maxLines;
    return el.scrollHeight > Math.ceil(maxHeight) + 2; // +2px tolerance
  }

  _exceedsChars(maxChars) {
    return (this._content.textContent || '').trim().length > maxChars;
  }

  _applyClamp(maxLines, maxChars) {
    const el = this._content;

    if (maxLines > 0) {
      // CSS line-clamp approach
      el.style.setProperty('--read-more-lines', maxLines);
      el.classList.add('read-more__content--clamped', 'read-more__content--lines');
    } else {
      // Character approach: store full HTML, show truncated
      this._fullHTML = el.innerHTML;
      this._truncatedHTML = this._truncateHTML(el, maxChars);
      el.innerHTML = this._truncatedHTML;
      el.classList.add('read-more__content--clamped', 'read-more__content--chars');
    }
  }

  /* Truncate inner HTML to `maxChars` visible characters, preserving tags */
  _truncateHTML(el, maxChars) {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    let count = 0;
    const ranges = [];

    let node;
    while ((node = walker.nextNode())) {
      const text = node.textContent;
      if (count + text.length >= maxChars) {
        ranges.push({ node, cut: maxChars - count });
        count = maxChars;
        break;
      }
      count += text.length;
    }

    if (ranges.length === 0) return el.innerHTML; // Nothing to cut

    // Clone element, then cut the text node
    const clone = el.cloneNode(true);
    const cloneWalker = document.createTreeWalker(clone, NodeFilter.SHOW_TEXT);
    let idx = 0;
    let cloneNode;
    while ((cloneNode = cloneWalker.nextNode())) {
      if (idx === ranges.length - 1) {
        cloneNode.textContent = cloneNode.textContent.slice(0, ranges[0].cut) + '\u2026';
        // Remove all subsequent siblings / parent siblings
        this._removeAfter(cloneNode);
        break;
      }
      idx++;
    }
    return clone.innerHTML;
  }

  _removeAfter(textNode) {
    let node = textNode;
    while (node && node !== this._content) {
      let sibling = node.nextSibling;
      while (sibling) {
        const next = sibling.nextSibling;
        sibling.parentNode.removeChild(sibling);
        sibling = next;
      }
      node = node.parentNode;
    }
  }

  /* ─── Button ─────────────────────────────────────────────────── */

  _buildButton() {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'read-more__btn';
    btn.setAttribute('aria-expanded', 'false');
    btn.textContent = this.labelMore;

    btn.addEventListener('click', () => this._toggle(btn));

    this.appendChild(btn);
    this._btn = btn;
  }

  _toggle(btn) {
    this._expanded = !this._expanded;
    const el = this._content;

    if (this._expanded) {
      // Expand
      if (el.classList.contains('read-more__content--chars')) {
        el.innerHTML = this._fullHTML;
      }
      el.classList.remove('read-more__content--clamped');
      btn.textContent = this.labelLess;
      btn.setAttribute('aria-expanded', 'true');
    } else {
      // Collapse
      if (el.classList.contains('read-more__content--chars')) {
        el.innerHTML = this._truncatedHTML;
      }
      el.classList.add('read-more__content--clamped');
      btn.textContent = this.labelMore;
      btn.setAttribute('aria-expanded', 'false');

      // Scroll back to element top if it's scrolled out of view
      const rect = this.getBoundingClientRect();
      if (rect.top < 0) {
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  _cleanup() {
    this._initialized = false;
  }
}

customElements.define('read-more', ReadMore);

/* ─────────────────────────────────────────────────────────────────
   MutationObserver — handles dynamic content (e.g. product recs
   rendered via fetch/API after initial page load).
   Watches the entire body for new <read-more> elements being added.
───────────────────────────────────────────────────────────────── */
(function watchDynamicReadMore() {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;

        // The added node itself
        if (node.tagName === 'READ-MORE') {
          requestAnimationFrame(() => node._init?.());
        }

        // Descendants inside the added node
        node.querySelectorAll('read-more').forEach((el) => {
          requestAnimationFrame(() => el._init?.());
        });
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();